import {Select} from '@wandb/weave/components/Form/Select';
import {type SortBy} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientTypes';
import {useRootObjectVersions} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/tsDataModelHooks';
import {type ObjectVersionFilter} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/wfDataModelHooksInterface';
import {SmallRefIcon} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/smallRef/SmallRefIcon';
import {parseRefMaybe} from '@wandb/weave/react';
import {makeRefObject} from '@wandb/weave/util/refs';
import React, {useMemo} from 'react';

type MonitorOption = {
  label: string;
  value: string;
  // All scorer op refs from the monitor
  scorerRefs: string[];
};

const MONITOR_FILTER: ObjectVersionFilter = {
  baseObjectClasses: ['Monitor'],
  latestOnly: true,
};

const MONITOR_SORT: SortBy[] = [{field: 'createdAtMs', direction: 'desc'}];

const renderOptionLabel = ({label}: MonitorOption) => (
  <span className="flex items-center gap-4 whitespace-nowrap">
    <SmallRefIcon icon="job-automation" />
    {label}
  </span>
);

export type MonitorSelection = {
  monitorRef: string;
  // All scorer op refs from the monitor
  scorerRefs: string[];
};

type MonitorSelectorProps = {
  entityName: string;
  projectName: string;
  value: string;
  onChange: (selection: MonitorSelection) => void;
};

export const MonitorSelector = ({
  entityName,
  projectName,
  value,
  onChange,
}: MonitorSelectorProps) => {
  const {result, loading, error} = useRootObjectVersions({
    entity: entityName,
    project: projectName,
    filter: MONITOR_FILTER,
    metadataOnly: false,
    sortBy: MONITOR_SORT,
  });

  const options = useMemo<MonitorOption[]>(() => {
    if (!result) {
      return [];
    }
    return result.map(monitor => {
      // Extract all scorer op refs from the monitor
      const rawScorers = monitor.val['scorers'];
      const scorerRefs =
        Array.isArray(rawScorers) &&
        rawScorers.every(s => typeof s === 'string')
          ? (rawScorers as string[])
          : [];

      return {
        label: monitor.val['name'] || monitor.objectId,
        value: makeRefObject(
          entityName,
          projectName,
          'object',
          monitor.objectId,
          monitor.versionHash,
          undefined
        ),
        scorerRefs,
      };
    });
  }, [result, entityName, projectName]);

  const selectedOption = useMemo(
    () => options.find(opt => isSameMonitorRef(opt.value, value)) ?? null,
    [options, value]
  );

  const placeholder = useMemo(() => {
    if (loading) {
      return 'Loading monitors...';
    }
    if (error) {
      return 'Error loading monitors';
    }
    if (options.length === 0) {
      return 'No monitors found';
    }
    return 'Select a monitor...';
  }, [loading, error, options.length]);

  return (
    <Select<MonitorOption, false>
      options={options}
      value={selectedOption}
      isDisabled={loading || options.length === 0}
      placeholder={placeholder}
      formatOptionLabel={renderOptionLabel}
      onChange={option =>
        onChange({
          monitorRef: option?.value ?? '',
          scorerRefs: option?.scorerRefs ?? [],
        })
      }
    />
  );
};

/**
 * Compare two monitor refs, ignoring version differences.
 * Matches on entity, project, kind, and artifact name.
 */
export const isSameMonitorRef = (leftRef: string, rightRef: string) => {
  if (leftRef === rightRef) {
    return true;
  }

  const left = parseRefMaybe(leftRef);
  const right = parseRefMaybe(rightRef);
  if (left == null || right == null) {
    return false;
  }

  // Monitor refs are always weave refs
  if (left.scheme !== 'weave' || right.scheme !== 'weave') {
    return false;
  }

  return (
    left.entityName === right.entityName &&
    left.projectName === right.projectName &&
    left.weaveKind === right.weaveKind &&
    left.artifactName === right.artifactName
  );
};
